// Pricing migration 32 (ADO-3985), T12: "See deal" on the product page scrolls to the deal section,
// highlights it and moves focus to its heading. It never opens the dialog (d/05, m/05).
//
// The confirmed d/05 highlight entry, from the note "scrolls to the "This multi-buy" card and
// highlights it (focus-ring outline, ~1.5s), then moves focus to its heading":
// - scroll: smooth (instant under reduced motion) to the section top, offset by the sticky
//   sub-nav's measured height plus `--space-4`;
// - focus: the section's `<h2>` (`tabindex="-1"`), with `preventScroll` so the scroll above is not
//   overridden;
// - highlight: shown at once, held DEAL_HIGHLIGHT_HOLD_MS, then faded out with
//   `--motion-exit-base` and `--ease-exit` (the CSS), or cut with no fade under reduced motion;
// - a press during a highlight restarts it rather than stacking it; unmounting clears the timers.
//
// The page owns one of these and hands `reveal` to the hero's strips and the rest to the section.
import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import { MOTION_MS, prefersReducedMotion } from '../../design-system';

/** How long the highlight holds before it fades: d/05's "~1.5s". No token exists for it. */
export const DEAL_HIGHLIGHT_HOLD_MS = 1500;

/** The sticky sub-nav the scroll keeps the section clear of (`SiteHeader`'s `.subNav`). */
const SUB_NAV_SELECTOR = '[data-sub-nav]';

/** `on`: the ring shows; `fading`: it fades out; `null`: none. */
export type DealHighlight = 'on' | 'fading' | null;

export interface DealSectionReveal {
  readonly sectionRef: RefObject<HTMLElement | null>;
  readonly headingRef: RefObject<HTMLHeadingElement | null>;
  readonly highlight: DealHighlight;
  /** Scroll to the section, move focus to its heading and highlight it. A no-op with no section
   *  on screen. */
  readonly reveal: () => void;
}

/** `--space-4` in px, read from the tokens; 0 when the tokens are not loaded (tests). */
function spaceFourPx(): number {
  const value = getComputedStyle(document.documentElement).getPropertyValue('--space-4');
  const px = Number.parseFloat(value);
  return Number.isFinite(px) ? px : 0;
}

export function useDealSectionReveal(): DealSectionReveal {
  const sectionRef = useRef<HTMLElement | null>(null);
  const headingRef = useRef<HTMLHeadingElement | null>(null);
  const [highlight, setHighlight] = useState<DealHighlight>(null);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearTimers = useCallback(() => {
    for (const timer of timers.current) clearTimeout(timer);
    timers.current = [];
  }, []);

  useEffect(() => clearTimers, [clearTimers]);

  const reveal = useCallback(() => {
    const section = sectionRef.current;
    if (section === null) return;
    const reducedMotion = prefersReducedMotion();

    const subNavHeight =
      document.querySelector(SUB_NAV_SELECTOR)?.getBoundingClientRect().height ?? 0;
    window.scrollTo({
      top: window.scrollY + section.getBoundingClientRect().top - subNavHeight - spaceFourPx(),
      behavior: reducedMotion ? 'auto' : 'smooth',
    });
    headingRef.current?.focus({ preventScroll: true });

    clearTimers();
    setHighlight('on');
    timers.current.push(
      setTimeout(() => {
        if (reducedMotion) {
          setHighlight(null);
          return;
        }
        setHighlight('fading');
        timers.current.push(setTimeout(() => setHighlight(null), MOTION_MS.exitBase));
      }, DEAL_HIGHLIGHT_HOLD_MS),
    );
  }, [clearTimers]);

  return { sectionRef, headingRef, highlight, reveal };
}
